import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";

export const PublicEnquiry = ({ enquiry, onChange, onClose }) => {
  const [sending, setSending] = useState(false);
  const viewing = enquiry.kind === "viewing";

  const send = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/sms/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(enquiry),
      });
      if (!res.ok)
        throw new Error((await res.json()).detail || "Could not send");
      toast.success(
        viewing
          ? "Thanks — we'll text you today to set a time"
          : "Thanks — we'll text you right back",
      );
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not send your message");
    }
    setSending(false);
  };

  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  return (
    <div
      className="site site-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
      data-testid="enquiry-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Text us about this slab"
    >
      <div
        className="w-full max-w-md border border-[color:var(--s-line)] bg-[color:var(--s-surface)] p-8"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="flex items-start gap-4">
          <div className="min-w-0">
            <h2 className="s-display text-2xl" data-testid="enquiry-title">
              {viewing ? "Arrange a viewing" : "Text us about this slab"}
            </h2>
            <p className="s-micro mt-3 truncate text-[color:var(--s-gold)]">
              {enquiry.item}
            </p>
          </div>
          <button
            type="button"
            data-testid="enquiry-close"
            onClick={onClose}
            className="ml-auto text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <Input
          data-testid="enquiry-name"
          value={enquiry.name}
          onChange={(e) => onChange({ ...enquiry, name: e.target.value })}
          placeholder="Your name"
          className="mt-8 h-12"
        />
        <Input
          data-testid="enquiry-phone"
          value={enquiry.phone}
          onChange={(e) => onChange({ ...enquiry, phone: e.target.value })}
          placeholder="Your mobile number"
          className="mt-3 h-12"
        />
        <Textarea
          data-testid="enquiry-message"
          rows={3}
          value={enquiry.message}
          onChange={(e) => onChange({ ...enquiry, message: e.target.value })}
          className="mt-3"
        />
        <label
          data-testid="enquiry-consent"
          className="mt-4 flex cursor-pointer gap-3 border border-[color:var(--s-line)] p-3"
        >
          <input
            type="checkbox"
            data-testid="enquiry-optin"
            checked={!!enquiry.sms_opt_in}
            onChange={(e) =>
              onChange({ ...enquiry, sms_opt_in: e.target.checked })
            }
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#c39e6d]"
          />
          <span className="text-[11px] leading-relaxed text-[color:var(--s-mute)]">
            Text me back at this number about this slab. Consent is not a
            condition of purchase. Message and data rates may apply; reply STOP
            to opt out, HELP for help. See our{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Privacy policy
            </a>
            .
          </span>
        </label>
        <button
          type="button"
          data-testid="enquiry-send"
          disabled={
            sending ||
            !enquiry.name.trim() ||
            !enquiry.phone.trim() ||
            !enquiry.sms_opt_in
          }
          onClick={send}
          className="s-btn mt-4 w-full disabled:pointer-events-none disabled:opacity-40"
        >
          {sending ? "Sending…" : viewing ? "Request a viewing" : "Send message"}
        </button>
        <p className="mt-4 text-center text-[11px] text-[color:var(--s-mute)]">
          We&apos;ll only use your number to reply about this enquiry.
        </p>
      </div>
    </div>
  );
};
